import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Container, Stack, Typography, IconButton, Box, Skeleton } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import CustomTabs from '../components/CustomTabs/ReviewTabs';
import UserInfoTab from '../sections/@dashboard/userList/UserInfoTab';
import HousingPreferences from '../sections/@dashboard/userList/HousingPreferences';
import { getUserDetails, getUsers } from '../network/apis';

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [tabValue, setTabValue] = useState(0);
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);

  useEffect(() => {
    if (!user && id) {
      setLoading(true);
      getUserDetails(id)
        .then((res) => {
          const userData = res?.data?.DATA || res?.data;
          if (userData) {
            setUser(userData);
          } else {
            // fallback search
            getUsers(0, '', '')
              .then((listRes) => {
                const found = listRes?.data?.content?.find((u) => u?.userId?.toString() === id?.toString());
                if (found) setUser(found);
              })
              .catch(() => {});
          }
        })
        .catch(() => {
          getUsers(0, '', '')
            .then((listRes) => {
              const found = listRes?.data?.content?.find((u) => u?.userId?.toString() === id?.toString());
              if (found) setUser(found);
            })
            .catch(() => {});
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, user]);

  const tabs = [t('users.userInfo') || 'معلومات المستخدم', t('users.housingPreferences') || 'تفضيلات الاسكان'];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    navigate('/dashboard/users');
  };

  return (
    <>
      <Helmet>
        <title>{t('users.profile') || 'الملف المستخدم'}</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={handleBack}
              sx={{
                p: 1,
                backgroundColor: '#fff',
                border: '1px solid #E5E8EB',
                borderRadius: '10px',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <ArrowBackIosIcon
                sx={{
                  fontSize: '18px',
                  color: '#212B36',
                  transform: isRtl ? 'rotate(180deg)' : 'none',
                }}
              />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827' }}>
              {user?.fullName ? `${t('users.profile')} - ${user.fullName}` : t('users.profile')}
            </Typography>
          </Box>

          {/* Main Card with Tabs */}
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              p: { xs: 2, md: 3 },
              boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <CustomTabs value={tabValue} onChange={handleTabChange} tabs={tabs} />
            </Box>

            {loading ? (
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={60} />
                <Skeleton variant="rounded" height={60} />
                <Skeleton variant="rounded" height={60} />
              </Stack>
            ) : (
              <>
                {tabValue === 0 && <UserInfoTab user={user} />}
                {tabValue === 1 && <HousingPreferences user={user} userId={id} />}
              </>
            )}
          </Box>
        </Stack>
      </Container>
    </>
  );
}
